import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { TitleIdToAction, ATF_DECORATION_SCHEME, BTF_DECORATION_SCHEME } from '../reducers/action'
import {
  TitleIdToDetail, DETAIL_FRAGMENT, HEADER_DETAIL_FRAGMENT
} from '../reducers/detail'
import { TitleIdToRefund, IRefundState } from '../reducers/refund'
import { TitleIdToImdb } from '../reducers/imdb'
import { TitleIdToNotification } from '../reducers/notification'
import { SeasonInfo, TitleIdToSeasonInfos } from '../reducers/seasons'
import { TitleIdToBuyBoxTitleId } from '../reducers/buyboxTitleId'
import { TitleIdToSelf } from '../reducers/self'
import { TitleIdToRestriction } from '../reducers/restriction'
import { TitleIdToTrailer } from '../reducers/trailer'
import { TitleIdToWatchlist } from '../reducers/watchlist'
import { TitleIdToOffer } from '../reducers/offerAvailableOn'
import { TitleIdToFormattedOffer } from '../reducers/formattedOfferAvailableOn'
import { TitleIdToFaq } from '../reducers/faq'
import { TitleIdToContactUs } from '../reducers/contactUs'
import { TitleIdToPageLink } from '../reducers/pageLink'
import { TitleIdToScoutBanner } from '../reducers/scoutBanner'
import { TitleIdToCastAndCrew } from '../reducers/castAndCrew'
import { IDetailPageState } from '../reducers'
import { TitleIdToCreative } from '../reducers/creative'
import { PageContext } from '../reducers/pageContext'
import { TitleIdToCrowBanner, UiBanner } from '../reducers/banner'
import { TitleIdToOtherFormats } from '../reducers/otherFormats'
import { BottomBar } from '../reducers/bottomBar'
import { GetDevicesSettingsOutput, SettingsOutput, SettingsResult, SettingsWidgetType, ClientsideLogger } from '@utils'
import { DeviceEntries, Devices, emptyDeviceEntries } from '../reducers/devices'

const logger = new ClientsideLogger('DVWebNode:detail')
const toLowerCase = (str?: string) => (str && str.toLowerCase()) || ''

//TODO : Move all this logic to WPS to avoid unnecessarily increasing bundle size
//https://sim.amazon.com/issues/AVWebDetailPage-2944
const normalizeDetail = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  [DETAIL_FRAGMENT]: {
    ...(reply.widgets.titleContent || []).reduce(
      (acc, collection) => {
        collection.cards.forEach(card => {
          acc[card.titleID] = card.detail
        })
        return acc
      },
      {} as TitleIdToDetail
    ),
    ...(reply.widgets.carousel || []).reduce(
      (acc, collection) => {
        collection.cards.forEach(card => {
          acc[card.titleID] = card.detail
        })
        return acc
      },
      {} as TitleIdToDetail
    ),
    ...(reply.widgets.castAndCrew ? reply.widgets.castAndCrew.castAndCrews : []).reduce(
      (acc, castMember) => {
        castMember.titleCollections.cards.forEach(card => {
          acc[card.titleID] = card.detail
        })
        return acc
      },
      {} as TitleIdToDetail
    ),
    ...(reply.widgets.seasonSelector || []).reduce(
      (acc, season) => {
        acc[season.titleID] = season.detail
        return acc
      },
      {} as TitleIdToDetail
    ),
    ...(reply.widgets.productDetails
      ? {[pageTitleId]: reply.widgets.productDetails.detail}
      : ({} as TitleIdToDetail)
    ),
    ...(reply.widgets.bundleCarousel && reply.widgets.bundleCarousel.cards || []).reduce(
      (acc, card) => {
        acc[card.titleID] = card.detail
        return acc
      },
      {} as TitleIdToDetail
    ),
  },
  [HEADER_DETAIL_FRAGMENT]: {
    ...(reply.widgets.header
      ? {[pageTitleId]: reply.widgets.header.detail}
      : ({} as TitleIdToDetail))
  }
})

const normalizeSelf = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.self
    ? { [pageTitleId]: reply.widgets.self }
    : ({} as TitleIdToSelf)),
  ...(reply.widgets.seasonSelector || []).reduce(
    (acc, season) => {
      acc[season.titleID] = season.self
      return acc
    },
    {} as TitleIdToSelf
  ),
  ...(reply.widgets.titleContent || []).reduce(
    (acc, collection) => {
      collection.cards
        .filter(card => card.self)
        .forEach(card => {
          acc[card.titleID] = card.self
        })
      return acc
    },
    {} as TitleIdToSelf
  ),
})

const normalizeImdb = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.header
    ? { [pageTitleId]: reply.widgets.header.imdb }
    : ({} as TitleIdToImdb))
})

const normalizeNotification = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.notification
    ? { [pageTitleId]: reply.widgets.notification }
    : ({} as TitleIdToNotification))
})

const normalizeSeasons = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.seasonSelector
    ? { [pageTitleId]: reply.widgets.seasonSelector.map(card => (
      { seasonId: card.titleID, seasonLink: card.seasonLink } as SeasonInfo))}
    : ({} as TitleIdToSeasonInfos))
})

const normalizeBuyboxTitleId = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.buybox
    ? { [pageTitleId]: reply.widgets.buybox.titleID }
    : ({} as TitleIdToBuyBoxTitleId))
})

const normalizeCreative = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.creative
    ? { [pageTitleId]: reply.widgets.creative }
    : ({} as TitleIdToCreative))
})

const normalizePageContext = (
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => (reply.widgets.pageContext as PageContext)

const normalizeRestriction = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.restrictions
    ? { [pageTitleId]: reply.widgets.restrictions }
    : ({} as TitleIdToRestriction))
})

const normalizeTrailer = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.trailer
    ? { [pageTitleId]: reply.widgets.trailer }
    : ({} as TitleIdToTrailer))
})

const normalizeWatchlist = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.watchlist
    ? {[pageTitleId]: reply.widgets.watchlist}
    : ({} as TitleIdToWatchlist))
})

const normalizeHoverWatchlist = (
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.carousel || []).reduce(
    (acc, collection) => {
      collection.cards
        .filter(card => card.watchlist)
        .forEach(card => {
          acc[card.titleID] = card.watchlist
        })
      return acc
    },
    {} as TitleIdToWatchlist
  ),
  ...(reply.widgets.titleContent || []).reduce(
    (acc, collection) => {
      collection.cards
        .filter(card => card.watchlist)
        .forEach(card => {
          acc[card.titleID] = card.watchlist
        })
      return acc
    },
    {} as TitleIdToWatchlist
  ),
  ...(reply.widgets.castAndCrew && reply.widgets.castAndCrew.castAndCrews.map(cast => cast.titleCollections) || []).reduce(
    (acc, collection) => {
      collection.cards
        .filter(card => card.watchlist)
        .forEach(card => {
          acc[card.titleID] = card.watchlist
        })
      return acc
    },
    {} as TitleIdToWatchlist
  ),
  ...(reply.widgets.bundleCarousel && reply.widgets.bundleCarousel.cards || []
      .filter(card => card.watchlist)
      .reduce((acc, card) => {
          acc[card.titleID] = card.watchlist
          return acc
        },
        {} as TitleIdToWatchlist)
  )
})

const normalizeAction = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  [BTF_DECORATION_SCHEME]: (reply.widgets.titleContent || []).reduce(
    (acc, collection) => {
      collection.cards.forEach(card => {
        if (card.action) {
          acc[card.titleID] = card.action
        }
      })
      return acc
    },
    {} as TitleIdToAction
  ),
  [ATF_DECORATION_SCHEME]: reply.widgets.buybox
    ? { [pageTitleId]: reply.widgets.buybox.action }
    : ({} as TitleIdToAction),
})

const normalizeRefund = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  fragments: {
    ...(reply.widgets.titleContent || []).reduce((acc, collection) => {
      collection.cards.forEach(card => {
        if (card.refund) {
          acc[card.titleID] = card.refund
        }
      })
      return acc
    }, {} as TitleIdToRefund),
    ...(reply.widgets.buybox
      ? { [pageTitleId]: reply.widgets.buybox.refund }
      : ({} as TitleIdToRefund)),
  },
  refunding: null
} as IRefundState)

const normalizeCollection = (collection: WpsRuntime.atv.wps.DetailPageCollectionModelProps) => ({
  header: collection.text,
  collectionType: toLowerCase(collection.collectionType),
  totalCardSize: collection.totalCardSize,
  titleIds: collection.cards.map(card => card.titleID)
})

const normalizeCollections = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => (
  reply.widgets.titleContent && reply.widgets.titleContent.length > 0
    ? {
      [pageTitleId]: reply.widgets.titleContent.map(collection => normalizeCollection(collection))
    }
    : {}
)


const normalizeCarousel = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => (
  reply.widgets.carousel && reply.widgets.carousel.length > 0
    ? {
      [pageTitleId]: reply.widgets.carousel.map(collection => normalizeCollection(collection))
    }
    : {}
)

const normalizeBundleCarousel = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => (
  reply.widgets.bundleCarousel
    ? {
      [pageTitleId]: normalizeCollection(reply.widgets.bundleCarousel)
    }
    : {}
)

const normalizeBanner = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  crow: reply.widgets.banner
    ? { [pageTitleId]: reply.widgets.banner }
    : ({} as TitleIdToCrowBanner),
  ui: null as UiBanner,
})

const normalizeFaq = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.faq
    ? { [pageTitleId]: reply.widgets.faq }
    : ({} as TitleIdToFaq))
})

const normalizeContactUs = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.contactUs
    ? { [pageTitleId]: reply.widgets.contactUs }
    : ({} as TitleIdToContactUs))
})

const normalizePageLink = (
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.carousel || []).reduce(
    (acc, collection) => {
      collection.cards.forEach(card => {
        acc[card.titleID] = card.pageLink
      })
      return acc
    },
    {} as TitleIdToPageLink
  ),
  ...(reply.widgets.castAndCrew && reply.widgets.castAndCrew.castAndCrews.map(cast => cast.titleCollections) || []).reduce(
    (acc, collection) => {
      collection.cards
        .filter(card => card.pageLink)
        .forEach(card => {
        acc[card.titleID] = card.pageLink
      })
      return acc
    },
    {} as TitleIdToPageLink
  ),
  ...(reply.widgets.titleContent || []).reduce(
    (acc, collection) => {
      collection.cards.forEach(card => {
        acc[card.titleID] = card.pageLink
      })
      return acc
    },
    {} as TitleIdToPageLink
  )}
)

const normalizeScoutBanner = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.scoutBanner
    ? { [pageTitleId]: reply.widgets.scoutBanner }
    : ({} as TitleIdToScoutBanner))
})

const normalizeCastAndCrew = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.castAndCrew
    ? { [pageTitleId]: reply.widgets.castAndCrew }
    : ({} as TitleIdToCastAndCrew))
})

const normalizeOtherFormats = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => ({
  ...(reply.widgets.otherFormats
    ? { [pageTitleId]: reply.widgets.otherFormats }
    : ({} as TitleIdToOtherFormats))
})

const normalizeBottomBar = (
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => (reply.widgets.bottomBar as BottomBar)

export const normalizeGetDetailPage = (
  titleId: string,
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps
) => {
  const pageTitleId = normalizePageTitleId(reply)

  if (!pageTitleId) {
    logger.fatal(`Failed to get pageTitleId from GetDetailPage response for ${titleId}`)
    logger.count('DVWeb::Detail::Normalize::Missing::PagetitleId', 1, true)
    return {}
  }

  return {
    pageTitleId,
    detail: normalizeDetail(pageTitleId, reply),
    action: normalizeAction(pageTitleId, reply),
    refund: normalizeRefund(pageTitleId, reply),
    imdb: normalizeImdb(pageTitleId, reply),
    bundleCarousel: normalizeBundleCarousel(pageTitleId, reply),
    buyboxTitleId: normalizeBuyboxTitleId(pageTitleId, reply),
    creative: normalizeCreative(pageTitleId, reply),
    banner: normalizeBanner(pageTitleId, reply),
    notification: normalizeNotification(pageTitleId, reply),
    seasons: normalizeSeasons(pageTitleId, reply),
    self: normalizeSelf(pageTitleId, reply),
    trailer: normalizeTrailer(pageTitleId, reply),
    watchlist: normalizeWatchlist(pageTitleId, reply),
    hoverWatchlist: normalizeHoverWatchlist(reply),
    restriction: normalizeRestriction(pageTitleId, reply),
    collections: normalizeCollections(pageTitleId, reply),
    carousel: normalizeCarousel(pageTitleId, reply),
    pageLink: normalizePageLink(reply),
    faq: normalizeFaq(pageTitleId, reply),
    contactUs: normalizeContactUs(pageTitleId, reply),
    scoutBanner: normalizeScoutBanner(pageTitleId, reply),
    castAndCrew: normalizeCastAndCrew(pageTitleId, reply),
    otherFormats: normalizeOtherFormats(pageTitleId, reply),
    pageContext: normalizePageContext(reply),
    bottomBar: normalizeBottomBar(reply),
  }
}

export type NormalizeGetDetailPageType = ReturnType<typeof normalizeGetDetailPage>

export const normalizePageTitleId = (
  reply: WpsRuntime.atv.wps.GetDetailPageOutputProps,
) => reply && reply.widgets && reply.widgets.pageContext.pageTitleId

export const normalizeGetTitles = (
  reply: WpsRuntime.atv.wps.GetTitlesOutputProps
) =>
  Object.keys(reply.results).reduce(
    (acc, titleId) => {
      const { action, refund } = reply.results[titleId]
      acc.action[BTF_DECORATION_SCHEME][titleId] = action
      acc.refund.fragments[titleId] = refund
      return acc
    },
    {
      action: { [ATF_DECORATION_SCHEME]: {}, [BTF_DECORATION_SCHEME]: {} },
      refund: {fragments: {}},
    } as Pick<IDetailPageState, 'action' | 'refund'>
  )

export type NormalizeGetTitlesType = ReturnType<typeof normalizeGetTitles>

export const normalizeGetPreferences = (
  reply: WpsRuntime.atv.wps.GetPreferencesOutputProps
) => ({
  customerPreferences: (reply && reply.customerBased) || null
})

export type NormalizeGetPreferencesType = ReturnType<
  typeof normalizeGetPreferences
>

export const normalizeGetStrings = (
  reply: WpsRuntime.atv.wps.GetStringsOutputProps
) => ({
  strings: reply.results
})

export type NormalizeGetStringsType = ReturnType<typeof normalizeGetStrings>

export const normalizeWatchlistToggle = (
  pageTitleId: string,
  reply: WpsRuntime.atv.wps.WatchlistToggleOutputProps
) => ({
  watchlist: {
    ...(reply.watchlistAction
      ? { [pageTitleId]: reply.watchlistAction }
      : ({} as TitleIdToWatchlist))
  }
})

export type NormalizeWatchlistToggleType = ReturnType<typeof normalizeWatchlistToggle>

export const normalizeGetDevices = (reply: SettingsOutput | null) => {
  let deviceEntries: DeviceEntries = emptyDeviceEntries
  if (reply && reply.widgets) {
    const result = reply.widgets.find(
      (widget: SettingsResult) => widget.widgetType === SettingsWidgetType.DevicesWidget && widget.content)
    if (result) {
      deviceEntries = (result.content as GetDevicesSettingsOutput).devices
    }
  }
  return { deviceEntries } as Devices
}

export type NormalizeGetDevicesType = ReturnType<typeof normalizeGetDevices>

export const normalizeOfferAvailableOn = (
  pageTitleId: string,
  offerAvailableOn?: string
) => ({
  ...(offerAvailableOn
    ? { offerAvailableOn: {[pageTitleId]: offerAvailableOn} }
    : ({} as TitleIdToOffer))
})

export const normalizeFormattedOfferAvailableOn = (
  pageTitleId: string,
  formattedOfferAvailableOn?: string
) => ({
  ...(formattedOfferAvailableOn
    ? { formattedOfferAvailableOn: {[pageTitleId]: formattedOfferAvailableOn} }
    : ({} as TitleIdToFormattedOffer))
})